import { ErrorDocument, ServiceStatus, ServiceHealth, StatusSummary, ServiceStatusDoc, SERVICES, SERVICE_DISPLAY_NAMES } from '@/types';
import { buildServiceHistory } from '@/services/history';

interface StatusThresholds {
  degraded: {
    errorCount: number;
    criticalCount: number;
  };
  outage: {
    errorCount: number;
    criticalCount: number;
  };
}

const DEFAULT_THRESHOLDS: StatusThresholds = {
  degraded: {
    errorCount: 5,     // 5+ errors in time window = degraded
    criticalCount: 1,  // 1+ critical error = degraded
  },
  outage: {
    errorCount: 20,    // 20+ errors = outage
    criticalCount: 3,  // 3+ critical = outage
  },
};

/**
 * When an error last occurred.
 *
 * `timestamp` records the FIRST sighting and is frozen at creation; dedup only
 * advances `lastSeen`. Every recency test on this page must use `lastSeen`, or
 * a long-lived recurring error reads as ancient history while it is actively
 * firing. Falls back to `timestamp` for any document written before `lastSeen`
 * existed.
 */
function lastOccurredAt(error: ErrorDocument): number {
  const seen = error.lastSeen ?? error.timestamp;
  return seen.toDate().getTime();
}

/**
 * Calculate service status based on recent errors
 */
export function calculateServiceStatus(
  errors: ErrorDocument[],
  timeWindowMs: number = 60 * 60 * 1000, // 1 hour default
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS
): ServiceStatus {
  const now = Date.now();
  const recentErrors = errors.filter(
    e => lastOccurredAt(e) > now - timeWindowMs
  );

  const criticalCount = recentErrors.filter(e => e.severity === 'critical').length;
  const highCount = recentErrors.filter(e => e.severity === 'high').length;
  const totalHighPlusCritical = criticalCount + highCount;

  // Check for outage
  if (
    criticalCount >= thresholds.outage.criticalCount ||
    (criticalCount >= 1 && highCount >= 5) ||
    totalHighPlusCritical >= thresholds.outage.errorCount
  ) {
    return 'outage';
  }

  // Check for degraded
  if (
    criticalCount >= thresholds.degraded.criticalCount ||
    highCount >= 3 ||
    totalHighPlusCritical >= thresholds.degraded.errorCount
  ) {
    return 'degraded';
  }

  return 'operational';
}

/**
 * Calculate status for all services
 */
export function calculateAllServicesStatus(
  errors: ErrorDocument[],
  timeWindowMs: number = 60 * 60 * 1000
): StatusSummary {
  const now = Date.now();
  const recentErrors = errors.filter(
    e => lastOccurredAt(e) > now - timeWindowMs && !e.resolved
  );

  // Group errors by service
  const errorsByService: Record<string, ErrorDocument[]> = {};
  for (const service of SERVICES) {
    errorsByService[service] = [];
  }

  for (const error of recentErrors) {
    const service = error.service || 'general';
    if (!errorsByService[service]) {
      errorsByService[service] = [];
    }
    errorsByService[service].push(error);
  }

  // Calculate status for each service
  const services: ServiceHealth[] = SERVICES.map(service => {
    const serviceErrors = errorsByService[service] || [];
    const status = calculateServiceStatus(serviceErrors, timeWindowMs);

    // Find most recent error. Copy before sorting — sort() mutates in place,
    // and this array is shared with errorsByService.
    let lastError: Date | undefined;
    if (serviceErrors.length > 0) {
      const mostRecent = [...serviceErrors].sort(
        (a, b) => lastOccurredAt(b) - lastOccurredAt(a)
      )[0];
      lastError = new Date(lastOccurredAt(mostRecent));
    }

    // errorCount counts distinct errors; occurrenceCount totals how many times
    // they actually fired. One deduplicated document can represent hundreds of
    // failures, and only the latter makes that visible.
    const occurrenceCount = serviceErrors.reduce(
      (total, e) => total + (e.count || 1),
      0
    );

    return {
      name: service,
      displayName: SERVICE_DISPLAY_NAMES[service] || service,
      status,
      errorCount: serviceErrors.length,
      occurrenceCount,
      lastError,
    };
  });

  // Calculate overall status
  let overall: ServiceStatus = 'operational';
  const hasOutage = services.some(s => s.status === 'outage');
  const hasDegraded = services.some(s => s.status === 'degraded');

  if (hasOutage) {
    overall = 'outage';
  } else if (hasDegraded) {
    overall = 'degraded';
  }

  return {
    overall,
    services,
    lastUpdated: new Date(),
  };
}

/**
 * Get severity display info
 */
export function getSeverityDisplay(severity: string): {
  label: string;
  color: string;
  bgColor: string;
} {
  switch (severity) {
    case 'critical':
      return {
        label: 'Critical',
        color: 'text-red-700',
        bgColor: 'bg-red-100',
      };
    case 'high':
      return {
        label: 'High',
        color: 'text-orange-700',
        bgColor: 'bg-orange-100',
      };
    case 'medium':
      return {
        label: 'Medium',
        color: 'text-amber-700',
        bgColor: 'bg-amber-100',
      };
    case 'low':
    default:
      return {
        label: 'Low',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100',
      };
  }
}


/**
 * Status from the PUBLIC aggregate (`serviceStatus/{service}`).
 *
 * Unlike calculateAllServicesStatus, which counts deduplicated error DOCUMENTS,
 * this counts actual occurrences inside the window — the metric the thresholds
 * were always meant to express. One error document representing 112 failures
 * used to score as a single "high", below the `high >= 3` degraded threshold.
 *
 * The window is applied here rather than by a backend job, so a service heals
 * on its own: once its entries age out, nothing counts and it reads operational.
 */
export function calculateStatusFromAggregates(
  aggregates: ServiceStatusDoc[],
  timeWindowMs: number = 60 * 60 * 1000,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS
): StatusSummary {
  const now = Date.now();
  const cutoff = now - timeWindowMs;
  const byService = new Map(aggregates.map(a => [a.service, a]));

  const services: ServiceHealth[] = SERVICES.map(service => {
    const recent = (byService.get(service)?.recent ?? []).filter(entry => {
      const at = entry?.ts?.toDate?.().getTime();
      return typeof at === 'number' && at > cutoff;
    });

    const criticalCount = recent.filter(e => e.sev === 'critical').length;
    const highCount = recent.filter(e => e.sev === 'high').length;
    const total = criticalCount + highCount;

    let status: ServiceStatus = 'operational';
    if (
      criticalCount >= thresholds.outage.criticalCount ||
      (criticalCount >= 1 && highCount >= 5) ||
      total >= thresholds.outage.errorCount
    ) {
      status = 'outage';
    } else if (
      criticalCount >= thresholds.degraded.criticalCount ||
      highCount >= 3 ||
      total >= thresholds.degraded.errorCount
    ) {
      status = 'degraded';
    }

    const times = recent
      .map(e => e.ts.toDate().getTime())
      .sort((a, b) => b - a);

    return {
      name: service,
      displayName: SERVICE_DISPLAY_NAMES[service] || service,
      status,
      // Each aggregate entry IS one occurrence, so these agree by construction.
      errorCount: total,
      occurrenceCount: total,
      lastError: times.length > 0 ? new Date(times[0]) : undefined,
      // Derived from the SAME entries as the verdict above, so the strip can
      // never disagree with the status word next to it.
      history: buildServiceHistory(byService.get(service), now),
    };
  });

  const overall: ServiceStatus = services.some(s => s.status === 'outage')
    ? 'outage'
    : services.some(s => s.status === 'degraded')
    ? 'degraded'
    : 'operational';

  return { overall, services, lastUpdated: new Date() };
}
